import { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Camera, Inbox, FileSpreadsheet, Building2, FileText, Mail, Users, Settings, PiggyBank, Tag, Wallet, ShieldCheck, SlidersHorizontal, LayoutDashboard, AlarmClock } from "lucide-react";
import { useLoad, branchPick } from "@/lib/api";
import { useMe, isManager } from "@/lib/me";
import { ROLE_LABEL } from "@/lib/format";
import { ThemeToggle } from "@/components/ThemeToggle";

const ALL = [
  { to: "/queries", label: "Queries", icon: Inbox, mgr: true },
  { to: "/overdue", label: "Overdue", icon: AlarmClock, mgr: true },
  { to: "/upload", label: "Upload", icon: Camera },
  { to: "/spend", label: "Spend", icon: Wallet, mgr: true },
  { to: "/statements", label: "Statements", icon: FileSpreadsheet, mgr: true },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/prices", label: "Prices", icon: Tag, mgr: true },
  { to: "/wholesalers", label: "Wholesalers", icon: Building2, mgr: true },
  { to: "/summary", label: "Summary", icon: PiggyBank, mgr: true },
  { to: "/emails", label: "Emails", icon: Mail, mgr: true },
  { to: "/team", label: "Team", icon: Users, mgr: true },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, mgr: true },
  { to: "/settings", label: "Settings", icon: SlidersHorizontal, mgr: true },
  { to: "/account", label: "Account", icon: Settings },
  { to: "/console", label: "Back office", icon: LayoutDashboard, admin: true },
];

function BranchPicker({ me }) {
  const b = useLoad(me.org?.branches > 1 ? "/branches" : null).data;
  if (!b || b.branches.length < 2) return null;
  const pick = (v) => { branchPick.set(v === "all" ? "" : v); window.location.reload(); };
  return (
    <select value={b.selected || "all"} onChange={(e) => pick(e.target.value)} className="mt-6 h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-2 text-sm font-semibold" data-testid="branch-picker">
      {me.allowed_branches == null && <option value="all">All branches</option>}
      {b.branches.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
    </select>
  );
}

const ACCESS_NOTE = {
  read_only: "Your payment is overdue. The account is read-only until the card is updated in Settings, Billing.",
  export_only: "Your subscription has ended. You can view and export your data for 90 days.",
};
const MOBILE = ["/queries", "/upload", "/spend", "/documents", "/account"];

const Brand = () => (
  <div className="flex items-center gap-3">
    <img src="/icon.svg" alt="" className="h-9 w-9" />
    <div className="leading-tight">
      <p className="font-display text-base font-extrabold" data-testid="brand-name">InvoiceReconciler<span className="text-teal-600">.app</span></p>
    </div>
  </div>
);

export default function Shell() {
  const me = useMe();
  const items = ALL.filter((i) => (i.admin ? me.admin : !i.mgr || isManager(me)));
  const pending = useLoad(isManager(me) ? "/deletions/count" : null);
  const reloadPending = pending.reload;
  useEffect(() => {
    if (!isManager(me)) return;
    window.addEventListener("deletions-changed", reloadPending);
    const t = setInterval(reloadPending, 60000);
    return () => { window.removeEventListener("deletions-changed", reloadPending); clearInterval(t); };
  }, [me, reloadPending]);
  const pendingN = pending.data?.pending || 0;
  const mobile = isManager(me) ? items.filter((i) => MOBILE.includes(i.to)) : items;
  const link = ({ isActive }) =>
    `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`;
  return (
    <div className="min-h-screen grain">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white p-4 md:flex print:!hidden">
        <Brand />
        <p className="mt-2 truncate text-xs font-semibold text-slate-500" data-testid="sidebar-org-name">{me.org?.name}</p>
        <BranchPicker me={me} />
        <nav className="mt-6 flex flex-1 flex-col gap-1 overflow-y-auto">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={link} data-testid={`nav-${label.toLowerCase()}-link`}>
              <Icon className="h-4 w-4" /> {label}
              {to === "/approvals" && pendingN > 0 && <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="approvals-badge">{pendingN}</span>}
            </NavLink>
          ))}
        </nav>
        <ThemeToggle className="mb-3" />
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
          <UserButton />
          <div className="min-w-0 text-xs">
            <p className="truncate font-semibold" data-testid="sidebar-user-email">{me?.email}</p>
            <p className="text-slate-500" data-testid="sidebar-user-role">{ROLE_LABEL[me?.role]}</p>
          </div>
        </div>
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/85 px-4 py-3 backdrop-blur-md md:hidden print:hidden">
        <Brand />
        <div className="flex items-center gap-3"><ThemeToggle compact testid="mobile-theme-toggle" /><UserButton /></div>
      </header>
      <main className="px-4 pb-28 pt-6 md:ml-64 md:px-10 md:pb-12 md:pt-10 print:m-0 print:p-0">
        <div className="mx-auto max-w-6xl">
          {ACCESS_NOTE[me.access] && <p className="mb-5 rounded-2xl bg-amber-50 p-4 text-sm font-semibold text-amber-900" data-testid="access-banner">{ACCESS_NOTE[me.access]}</p>}
          <div className="md:hidden"><BranchPicker me={me} /></div>
          <Outlet />
        </div>
      </main>
      <nav className="pb-safe fixed bottom-0 left-0 right-0 z-40 flex justify-around border-t border-slate-200 bg-white/90 px-2 pt-2 backdrop-blur-md md:hidden print:hidden">
        {mobile.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}-link`}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 rounded-lg py-1 text-[11px] font-semibold ${isActive ? "text-teal-700" : "text-slate-500"}`}>
            <span className="relative"><Icon className="h-5 w-5" />
              {to === "/account" && pendingN > 0 && <span className="absolute -right-2 -top-1 h-2.5 w-2.5 rounded-full bg-rose-600" data-testid="mobile-approvals-dot" />}</span> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
